// Safe handling of every URL a user types and another user is shown — instructor intro videos,
// social links, and the session meeting links students are told to click to join class.
//
// The backend keeps its own copy of `isSafeLinkUrl` in backend/src/lib/urls.js. There is no shared
// build between the two packages, and a hand-rolled cross-package import for ten lines is worse
// than the duplication; both are tested against the same cases.

// Read once at load and reuse (CLAUDE.md), and configurable rather than baked in. These are not
// per-environment endpoints, but the origin is a real operational lever: switching YouTube to
// https://www.youtube-nocookie.com is the privacy-preserving embed, and either provider can be
// pointed at a proxy, without a code change.
const YOUTUBE_EMBED_ORIGIN = import.meta.env.VITE_YOUTUBE_EMBED_ORIGIN ?? 'https://www.youtube.com';
const YOUTUBE_THUMBNAIL_ORIGIN = import.meta.env.VITE_YOUTUBE_THUMBNAIL_ORIGIN ?? 'https://img.youtube.com';
const LOOM_EMBED_ORIGIN = import.meta.env.VITE_LOOM_EMBED_ORIGIN ?? 'https://www.loom.com';

// Any URL we put in an href has to be one a browser can safely open. `javascript:` in an href runs
// on click, and these URLs are instructor-typed, so without this an instructor could run script in
// a visiting student's session on our own origin. Blocking the sink is what makes an
// already-stored value inert; validating on save is defence in depth (TTT-73).
export function isSafeLinkUrl(url) {
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false; // not a URL at all (relative, empty, malformed)
  }
}

// The host a link actually goes to, so a student can see where a Join button will send them
// before they click it. An allowlist is not airtight — allowed domains host open redirectors —
// and showing the destination is what lets someone notice when it looks wrong.
// Null when there is nothing safe to show.
export function linkHost(url) {
  if (!isSafeLinkUrl(url)) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

// Adding a provider is a row here, not another branch in the parser.
const VIDEO_PROVIDERS = [
  {
    kind: 'youtube',
    pattern: /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    embedUrl: (id) => `${YOUTUBE_EMBED_ORIGIN}/embed/${id}?autoplay=1`,
    // hqdefault, not maxresdefault — the latter 404s for plenty of videos. Even so the caller
    // handles a failed poster, because a 404 here would otherwise be a broken image icon.
    posterUrl: (id) => `${YOUTUBE_THUMBNAIL_ORIGIN}/vi/${id}/hqdefault.jpg`,
  },
  {
    kind: 'loom',
    pattern: /loom\.com\/share\/([a-zA-Z0-9]+)/,
    embedUrl: (id) => `${LOOM_EMBED_ORIGIN}/embed/${id}?autoplay=1`,
    // Loom publishes no thumbnail at a predictable URL, so this one gets the branded panel.
    posterUrl: () => null,
  },
];

// The one place that decides what an instructor's video link means, so the poster and the player
// can never disagree about it. Returns null for anything we can't embed — the caller shows a plain
// link instead of a broken frame.
//
// Only the captured id is ever interpolated into a provider URL, so an arbitrary (or `javascript:`)
// value cannot reach an iframe `src`; it falls through to null here.
export function parseIntroVideo(url) {
  if (typeof url !== 'string') return null;
  for (const provider of VIDEO_PROVIDERS) {
    const match = url.match(provider.pattern);
    if (match) {
      return {
        kind: provider.kind,
        embedUrl: provider.embedUrl(match[1]),
        posterUrl: provider.posterUrl(match[1]),
      };
    }
  }
  return null;
}
